import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { DirectionsCar, EvStation, LocalParking } from "@mui/icons-material";
import { TFunction } from "i18next";
import type { ReactNode } from "react";
import { BookingType } from "../../../Booking-Form/typings/booking-inputs";
import {
  IndependentBookingMode,
  IndependentBookingSetting
} from "../../../Login/typings/login.types";
import { ReactComponent as AvatarIcon } from "../../../Booking-Form/assets/avatar.svg";
import { ReactComponent as ConferenceIcon } from "../../../Booking-Form/assets/conference.svg";
import {
  AdvancedBookingPerTypeRowIcon,
  AdvancedPerTypeAccordion
} from "./CompanyCard-Setting-AdvancedPerTypeAccordion.partial";
import { BookingTypeEnablingRowConfig } from "./company-card-booking-type-enabling.config";
import {
  defaultIndependentBookingMode,
  getIndependentBookingModeLabelKey,
  INDEPENDENT_BOOKING_MODE_OPTIONS,
  INDEPENDENT_BOOKING_TYPE_ROW_IDS,
  INDEPENDENT_BOOKING_TYPE_TITLE_KEYS
} from "./company-card-independent-bookings.config";

type IndependentBookingsPerTypeProps = {
  visibleRows: BookingTypeEnablingRowConfig[];
  settings: IndependentBookingSetting[];
  isMobile: boolean;
  t: TFunction;
  onModeChange: (bookingType: BookingType, mode: IndependentBookingMode) => void;
};

export function IndependentBookingsPerType({
  visibleRows,
  settings,
  isMobile,
  t,
  onModeChange
}: Readonly<IndependentBookingsPerTypeProps>) {
  return (
    <>
      {visibleRows.map(row => {
        const bookingType = row.bookingType;
        if (bookingType == null) {
          return null;
        }

        const rowId = INDEPENDENT_BOOKING_TYPE_ROW_IDS[bookingType] ?? bookingType;
        const titleKey = INDEPENDENT_BOOKING_TYPE_TITLE_KEYS[bookingType];
        const currentMode =
          settings.find(setting => setting.bookingType === bookingType)?.mode ??
          defaultIndependentBookingMode(bookingType);

        if (titleKey == null) {
          return null;
        }

        return (
          <AdvancedPerTypeAccordion
            key={rowId}
            rowTestId={`independentBookings-${rowId}-row`}
            title={t(titleKey)}
            typeIcon={getIndependentBookingTypeIcon(bookingType)}
            contents={<span>{t(getIndependentBookingModeLabelKey(currentMode))}</span>}
          >
            <FormControl component="fieldset" variant="standard" fullWidth>
              <RadioGroup
                value={currentMode}
                row={!isMobile}
                onChange={(_e, value) => onModeChange(bookingType, value as IndependentBookingMode)}
              >
                {INDEPENDENT_BOOKING_MODE_OPTIONS.map(({ mode, labelKey, testIdSuffix }) => (
                  <FormControlLabel
                    key={mode}
                    value={mode}
                    control={
                      <Radio
                        data-testid={`independentBookings-${rowId}-mode-${testIdSuffix}`}
                        slotProps={{ input: { "aria-label": t(labelKey) } }}
                      />
                    }
                    label={t(labelKey)}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </AdvancedPerTypeAccordion>
        );
      })}
    </>
  );
}

function getIndependentBookingTypeIcon(bookingType: BookingType): ReactNode {
  switch (bookingType) {
    case BookingType.WORKPLACE:
      return <AdvancedBookingPerTypeRowIcon icon={<AvatarIcon width="18px" height="18px" />} />;
    case BookingType.PARKINGPLACE:
      return <AdvancedBookingPerTypeRowIcon icon={<LocalParking />} />;
    case BookingType.ELECTRICCHARGINGSTATIONPLACE:
      return <AdvancedBookingPerTypeRowIcon icon={<EvStation />} />;
    case BookingType.VEHICLE:
      return <AdvancedBookingPerTypeRowIcon icon={<DirectionsCar />} />;
    case BookingType.CONFERENCEZONE:
      return <AdvancedBookingPerTypeRowIcon icon={<ConferenceIcon width="18px" height="18px" />} />;
    default:
      return null;
  }
}
